import * as React from "react";
import { Switch, Typography, makeStyles, Theme, MenuItem, Select } from "@material-ui/core";

import BorderedBox from "../SharedComponents/BorderedBox";
import { HandicapRules } from "../../types/competition";
import HandicapControl from "./handicapControl";
import { HandicapAdjustmentRule } from "../../types/competition";
import PlacingControl from "../SharedComponents/PlacingControl";
import { HELP_VIDEOS } from "consts";
import Config from "../../config";

export interface CustomGameHandicapsProps {
  handicapRules: HandicapRules;
  scoringFormat?: string;
  onHandicapRulesChanged: (value: HandicapRules) => void;
}

const hcpAdjustmentNumbers = Array.from({ length: 21 }, (_, i) => i - 10);
const hcpAdjustmentPercents = Array.from({ length: 21 }, (_, i) => i * 5 - 50);

const CustomGameHandicaps: React.FunctionComponent<CustomGameHandicapsProps> = (props: CustomGameHandicapsProps) => {
  const classes = useStyles();
  const { handicapRules: rules, scoringFormat } = props;
  const showAverageSplit = scoringFormat === Config.SCORING_FORMAT.STABLEFORD;

  const hcpListItems = rules.usingNumberInFirstTimeHandicapAdjustment ? hcpAdjustmentNumbers : hcpAdjustmentPercents;

  const onSwitchChanged = (field: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked as boolean;
    if (field === "useSocialHcpIfWhsHiMissing") {
      props.onHandicapRulesChanged({
        ...props.handicapRules,
        useSocialHcpIfWhsHiMissing: checked,
        applySlopeForSocialHcp: false,
      });
      return;
    }
    if (field === "adjustHandicapOnAverageScore") {
      const handicapAdjustmentRules = (props.handicapRules.handicapAdjustmentRules || []).map(rule => {
        if (!checked) {
          return { ...rule, greaterThanIsAverage: false, lessThanIsAverage: false };
        }
        const value = Number(rule.value);
        return {
          ...rule,
          greaterThanIsAverage: value < 0,
          lessThanIsAverage: value > 0,
        };
      });
      props.onHandicapRulesChanged({
        ...props.handicapRules,
        adjustHandicapOnAverageScore: checked,
        handicapAdjustmentRules,
      });
      return;
    }
    props.onHandicapRulesChanged({
      ...props.handicapRules,
      [field]: checked,
    } as HandicapRules);
  };

  const onHandicapDuctingChanged = (result: number[]) => {
    props.onHandicapRulesChanged({
      ...props.handicapRules,
      handicapDeducting: result,
    } as HandicapRules);
  };

  const onLosersHandicapDuctingChanged = (result: number[]) => {
    props.onHandicapRulesChanged({
      ...props.handicapRules,
      losersHandicapDeducting: result,
    } as HandicapRules);
  };

  const onHandicapRulesChanged = (result: HandicapAdjustmentRule[]) => {
    props.onHandicapRulesChanged({
      ...props.handicapRules,
      handicapAdjustmentRules: result,
    } as HandicapRules);

    //  const { handicapRules } = this.state.competition;
    //  if (!handicapRules) return;
  };

  const onSelectChanged = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    if (!event.target.name) return;
    props.onHandicapRulesChanged({
      ...props.handicapRules,
      [event.target.name]: event.target.value as string,
    });
  };

  return (
    <BorderedBox title={"Handicap Rules"} variant={"h6"} videoIcon videoURL={HELP_VIDEOS.HANDICAP_RULES}>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        Social Handicaps
      </Typography>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>Use Social Hcp if WHS HI is not available ?</Typography>
        <Switch
          name="useSocialHcpIfWhsHiMissing"
          checked={!!rules.useSocialHcpIfWhsHiMissing}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>Apply slope calculation for Social Hcp ?</Typography>
        <Switch
          name="applySlopeForSocialHcp"
          checked={!!rules.useSocialHcpIfWhsHiMissing && !!rules.applySlopeForSocialHcp}
          disabled={!rules.useSocialHcpIfWhsHiMissing}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        General Handicaps
      </Typography>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>Remove slope calculation altogether</Typography>
        <Switch
          name="removeSlopeCalculation"
          checked={!!rules.removeSlopeCalculation}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>Remove course rating minus par calculation</Typography>
        <Switch
          name="removeCourseRatingMinusPar"
          checked={!!rules.removeCourseRatingMinusPar}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
      Do you:
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>Use decimal points: </Typography>
        <Switch
          name="exactHandicaps"
          value={rules.exactHandicaps}
          checked={rules.exactHandicaps}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>Mark all players with a zero (0) Playing Handicap</Typography>
        <Switch
          name="zeroHandicapRule"
          value={rules.zeroHandicapRule || false}
          checked={rules.zeroHandicapRule || false}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"} style={{ flex: 1 }}>
          Adjust a first time players playing handicap?
        </Typography>
      </div>
      <div className={classes.inline}>
        <div>
          %
          <Switch
            classes={{
              thumb: classes.thumb,
              track: classes.track,
            }}
            name="usingNumberInFirstTimeHandicapAdjustment"
            value={rules.usingNumberInFirstTimeHandicapAdjustment}
            checked={rules.usingNumberInFirstTimeHandicapAdjustment}
            onChange={e => onSwitchChanged(e.target.name, e)}
          />
          Num.
        </div>
        <Select name="firstTimeHandicapAdjustment" value={rules.firstTimeHandicapAdjustment} onChange={onSelectChanged}>
          {hcpListItems.map((item: number, index) => (
            <MenuItem value={item} key={index}>
              {rules.usingNumberInFirstTimeHandicapAdjustment ? item : `${item}%`}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>Operate handicap categories?</Typography>
        <Switch
          name="customHandicapCategories"
          value={rules.customHandicapCategories}
          checked={rules.customHandicapCategories}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>Adjust handicap on result position?</Typography>
        <Switch
          name="adjustHandicapOnResult"
          value={rules.adjustHandicapOnResult}
          checked={rules.adjustHandicapOnResult}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
      <div>
        <PlacingControl
          data={rules.handicapDeducting}
          isVisible={rules.adjustHandicapOnResult}
          onChanged={onHandicapDuctingChanged}
          defaultValue={-1}
          type={"winners"}
        />
      </div>
      <div className={classes.inline}></div>
      <div>
        <PlacingControl
          data={rules.losersHandicapDeducting}
          isVisible={rules.adjustHandicapOnResult}
          onChanged={onLosersHandicapDuctingChanged}
          defaultValue={1}
          type={"losers"}
        />
      </div>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>
          Can players go above their currently calculated PH when applying custom handicap adjustment?
        </Typography>
        <Switch
          name="aboveHIadjustment"
          value={rules.aboveHIadjustment}
          checked={rules.aboveHIadjustment}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>Adjust handicap on points scored?</Typography>
        <Switch
          name="adjustHandicapOnPoints"
          value={rules.adjustHandicapOnPoints}
          checked={rules.adjustHandicapOnPoints}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
      {rules.adjustHandicapOnPoints && showAverageSplit ? (
        <div>
          <div className={classes.inline}>
            <Typography variant={"subtitle1"}>Use Avg in the bands below?</Typography>
            <Switch
              name="adjustHandicapOnAverageScore"
              value={rules.adjustHandicapOnAverageScore}
              checked={!!rules.adjustHandicapOnAverageScore}
              onChange={e => onSwitchChanged(e.target.name, e)}
            />
          </div>
          {rules.adjustHandicapOnAverageScore ? (
            <Typography variant="caption" color="textSecondary" className={classes.avgHint}>
              Avg is the rounded average of all players' points when the game is closed.
            </Typography>
          ) : null}
        </div>
      ) : null}
      <div>
        <HandicapControl
          handicapAdjustments={rules.handicapAdjustmentRules}
          isVisible={rules.adjustHandicapOnPoints}
          allowAverageBound={showAverageSplit && !!rules.adjustHandicapOnAverageScore}
          onChanged={onHandicapRulesChanged}
        />
      </div>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>Disregard handicap increase if player records a DNP?</Typography>
        <Switch
          name="disregardDnp"
          value={rules.disregardDnp}
          checked={rules.disregardDnp}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
      <div className={classes.inline}>
        <Typography variant={"subtitle1"}>Do you allow joint/shared prizes: </Typography>
        <Switch
          name="sharedPrize"
          value={rules.sharedPrize}
          checked={rules.sharedPrize}
          onChange={e => onSwitchChanged(e.target.name, e)}
        />
      </div>
    </BorderedBox>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  inline: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  avgHint: {
    display: "block",
    margin: "0 0 8px",
    lineHeight: 1.4,
  },
  thumb: {
    color: theme.palette.secondary.main,
  },
  track: {
    backgroundColor: theme.palette.secondary.main,
  },
}));

export default CustomGameHandicaps;
